import { HTTPError } from 'ky';
import { api, failureMessage } from 'lib/request/api';
import { CASE_QUERY_KEY } from './query-keys';

/** The case read's timeout: the shared client's, named so a test pins it. */
export const CASE_READ_TIMEOUT_MS = 30_000;

/** A failed case read: its message, and the HTTP status when the server answered (null if not). */
export class CaseReadError extends Error {
  readonly status: number | null;

  /**
   * @param {string} message - The message the page shows.
   * @param {number | null} status - The HTTP status, or null when no answer came.
   */
  constructor(message: string, status: number | null) {
    super(message);
    this.name = 'CaseReadError';
    this.status = status;
  }
}

/**
 * Whether a failed case read was refused by the server (a 4xx): asking again gets the same answer.
 *
 * @param {unknown} error - What the read threw.
 * @return {boolean} True for a 4xx.
 */
export function isClientError(error: unknown): boolean {
  return (
    error instanceof CaseReadError &&
    error.status !== null &&
    error.status >= 400 &&
    error.status < 500
  );
}

export type RetrySetting =
  | boolean
  | number
  | ((failureCount: number, error: unknown) => boolean)
  | undefined;

/**
 * A query's retry rule: never a 4xx, otherwise the client's own setting (React Query's 3 when the
 * client sets none).
 *
 * @param {RetrySetting} clientRetry - The client's default `retry`.
 * @return {Function} The retry rule.
 */
export function retryUnlessClientError(clientRetry: RetrySetting) {
  return (failureCount: number, error: unknown): boolean => {
    if (isClientError(error)) return false;
    if (typeof clientRetry === 'function')
      return clientRetry(failureCount, error);
    if (typeof clientRetry === 'number') return failureCount < clientRetry;
    return clientRetry ?? failureCount < 3;
  };
}

/**
 * `GET /evaluation_cases/:id`, through the shared ky instance (its reload on a 401), with its
 * timeout (CASE_READ_TIMEOUT_MS). An aborted read (React Query cancelling it) rejects with the
 * abort; any other failure with a CaseReadError carrying the message and the HTTP status.
 *
 * @param {number} id - The case id.
 * @param {AbortSignal} [signal] - React Query's signal, so a read nobody waits for is cancelled.
 * @return {Promise<T>} The case, typed as the caller reads it.
 */
export async function fetchCase<T extends object>(
  id: number,
  signal?: AbortSignal
): Promise<T> {
  try {
    return await api
      .get(`evaluation_cases/${id}`, { signal, timeout: CASE_READ_TIMEOUT_MS })
      .json<T>();
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') throw error;
    throw new CaseReadError(
      (await failureMessage(error)) || 'Failed to load case',
      error instanceof HTTPError ? error.response.status : null
    );
  }
}

/**
 * The one query for `GET /evaluation_cases/:id`: its key and fetcher, shared by the case page
 * (useCase) and the eval page (useEvaluationCaseDetail), so the cache entry they share is always
 * filled the same way. Nothing is fetched for no case. Retries are each page's own: the case page
 * sets none (its Reload is the way back); the eval page takes the client's setting but never
 * retries a 4xx (retryUnlessClientError).
 *
 * @param {number | null} caseId - The case id, or null for none.
 * @return {object} The query options.
 */
export function caseQueryOptions<T extends object>(caseId: number | null) {
  return {
    queryKey: [CASE_QUERY_KEY, caseId] as const,
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      fetchCase<T>(caseId as number, signal),
    enabled: caseId !== null,
  };
}
